import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from 'sonner';
import { createManualQuote } from '../../services/api';

// Mêmes types que le formulaire public de soumission.
export const QUOTE_TYPES = [
  { value: 'climatisation', label: 'Climatisation & Chauffage' },
  { value: 'ventilation', label: 'Ventilation' },
  { value: 'reparation', label: "Réparation d'électroménagers" },
  { value: 'construction_neuve', label: 'Construction neuve' },
  { value: 'autre', label: 'Autre' },
];

const EMPTY = {
  first_name: '',
  last_name: '',
  phone: '',
  email: '',
  address: '',
  installation_type: 'climatisation',
  description: '',
  availability_text: '',
};

// Saisie d'une demande reçue par téléphone, courriel ou en personne.
export const NewQuoteDialog = ({ open, onOpenChange, onCreated }) => {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const set = (field) => (event) => setForm({ ...form, [field]: event.target.value });

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!form.phone.trim() && !form.email.trim()) {
      toast.error('Indiquez un téléphone ou un courriel');
      return;
    }
    setSaving(true);
    try {
      const payload = Object.fromEntries(
        Object.entries(form).map(([key, value]) => [key, typeof value === 'string' ? value.trim() : value])
      );
      ['phone', 'email', 'address', 'availability_text'].forEach((key) => {
        if (!payload[key]) payload[key] = null;
      });
      const { data } = await createManualQuote(payload);
      toast.success('Soumission créée');
      setForm(EMPTY);
      onOpenChange(false);
      onCreated?.(data);
    } catch (error) {
      const detail = error?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Vérifiez les champs (courriel valide, description)');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Nouvelle soumission</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4" data-testid="new-quote-form">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label>Prénom *</Label>
              <Input value={form.first_name} onChange={set('first_name')} maxLength={80} required data-testid="new-quote-first-name" />
            </div>
            <div>
              <Label>Nom</Label>
              <Input value={form.last_name} onChange={set('last_name')} maxLength={80} />
            </div>
            <div>
              <Label>Téléphone</Label>
              <Input type="tel" value={form.phone} onChange={set('phone')} maxLength={40} data-testid="new-quote-phone" />
            </div>
            <div>
              <Label>Courriel</Label>
              <Input type="email" value={form.email} onChange={set('email')} data-testid="new-quote-email" />
            </div>
          </div>
          <div>
            <Label>Adresse des travaux</Label>
            <Input value={form.address} onChange={set('address')} maxLength={300} />
          </div>
          <div>
            <Label>Type de travaux *</Label>
            <Select value={form.installation_type} onValueChange={(value) => setForm({ ...form, installation_type: value })}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {QUOTE_TYPES.map((type) => (
                  <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Description *</Label>
            <Textarea
              value={form.description}
              onChange={set('description')}
              maxLength={4000}
              required
              placeholder="Besoins du client, équipement, contraintes..."
              data-testid="new-quote-description"
            />
          </div>
          <div>
            <Label>Disponibilités</Label>
            <Input value={form.availability_text} onChange={set('availability_text')} maxLength={500} placeholder="Ex. : en semaine après 15 h" />
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Annuler
            </Button>
            <Button type="submit" disabled={saving} className="bg-[#00b4d8] hover:bg-[#009bb8]" data-testid="new-quote-submit">
              {saving ? 'Création...' : 'Créer la soumission'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default NewQuoteDialog;
